import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  AdminListCard,
  AdminListTable,
  ConfirmDeleteModal,
  PageTabs,
  saveErrorReason,
  useBatchSelection,
  withFailureReasons,
} from "@components/CatalogIndex";
import type { BatchFailure } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useSalesExecutivesList, useSalesExecutiveMutations } from "../api";
import {
  buildSalesExecutiveBatchActions,
  type SalesExecutiveTab,
} from "./batchActions";
import type { SalesExecutive } from "../types";

const PAGE_SIZE = 25;

const List = (): JSX.Element => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<SalesExecutiveTab>("active");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchInput, setSearchInput] = useState("");
  const [nameFilter, setNameFilter] = useState("");

  const sel = useBatchSelection([tab]);
  const { archive, restore } = useSalesExecutiveMutations();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const { rows, pageMeta, isLoading, isFetching, refetch } =
    useSalesExecutivesList({
      page: { number: pageNumber, size: pageSize },
      filter: {
        active: tab === "active",
        ...(nameFilter ? { name: nameFilter } : {}),
      },
    });

  const selectedExecutives = useMemo(
    () => rows.filter((r) => sel.selectedIds.includes(r.id)),
    [rows, sel.selectedIds]
  );

  const columns: ColumnDef<SalesExecutive>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => row.original.name,
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => row.original.email || "",
    },
    {
      accessorKey: "title",
      header: "Title",
      cell: ({ row }) => row.original.title || "",
    },
    {
      accessorKey: "phone",
      header: "Phone",
      cell: ({ row }) => row.original.phone || "",
    },
  ];

  const runOnSelected = async (
    fn: (se: SalesExecutive) => Promise<unknown>,
    successText: (n: number) => string
  ) => {
    const targets = [...selectedExecutives];
    const failures: BatchFailure[] = [];
    for (const se of targets) {
      try {
        await fn(se);
      } catch (err) {
        failures.push({ label: se.name, reason: saveErrorReason(err) });
      }
    }
    setToast(
      failures.length === 0
        ? { variant: "success", text: successText(targets.length) }
        : {
            variant: "danger",
            text: withFailureReasons(
              `Failed on ${failures.length} of ${targets.length} sales executive(s).`,
              failures
            ),
          }
    );
    sel.clear();
    refetch();
  };

  const handleConfirmDeleteOrRestore = async () => {
    setConfirmOpen(false);
    if (tab === "active") {
      await runOnSelected(
        (se) => archive(se.id),
        (n) => `${n} sales executive(s) archived.`
      );
    } else {
      await runOnSelected(
        (se) => restore(se.id, se.name),
        (n) => `${n} sales executive(s) restored.`
      );
    }
  };

  const batchActions = buildSalesExecutiveBatchActions(
    tab,
    sel.selectedIds.length,
    {
      onDelete: () => setConfirmOpen(true),
      onRestore: () => setConfirmOpen(true),
    }
  );

  const submitSearch = () => {
    setPageNumber(1);
    setNameFilter(searchInput);
  };

  if (isLoading) return <ScopeStackSpinner />;

  return (
    <>
      <AdminListCard
        title="Sales Executives"
        onNew={() => navigate("new")}
        newButtonLabel="+ Add A Sales Executive"
        searchProps={{
          label: "Search Sales Executives",
          placeholder: "Search by name",
          value: searchInput,
          onChange: (e) => setSearchInput(e.target.value),
          onSubmit: (e) => {
            e.preventDefault();
            submitSearch();
          },
          onClear: () => {
            setSearchInput("");
            setPageNumber(1);
            setNameFilter("");
          },
        }}
        batchActions={batchActions}
        selectedCount={sel.selectedIds.length}
      >
        <PageTabs
          activeKey={tab}
          onSelect={(key) => {
            setTab(key as SalesExecutiveTab);
            setPageNumber(1);
            sel.clear();
          }}
          tabs={[
            {
              key: "active",
              label: "Active",
              count: tab === "active" ? pageMeta.recordCount : undefined,
            },
            {
              key: "archived",
              label: "Archived",
              count: tab === "archived" ? pageMeta.recordCount : undefined,
            },
          ]}
        />
        <AdminListTable
          columns={columns}
          data={rows}
          isLoading={isFetching}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          onEdit={(id) => navigate(`${id}/edit`)}
          emptyMessage="No sales executives found."
          paginationEnabled
          pageSizeEnabled
          totalPages={pageMeta.pageCount}
          totalRows={pageMeta.recordCount}
          currentPage={pageNumber}
          setCurrentPage={(p) => {
            sel.clear();
            setPageNumber(p);
          }}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            sel.clear();
            setPageSize(size);
            setPageNumber(1);
          }}
        />
      </AdminListCard>

      <ConfirmDeleteModal
        open={confirmOpen}
        title={
          tab === "active"
            ? "Archive sales executives?"
            : "Restore sales executives?"
        }
        message={`Are you sure you want to ${
          tab === "active" ? "archive" : "restore"
        } ${sel.selectedIds.length} sales executive(s)?`}
        confirmLabel={tab === "active" ? "Archive" : "Restore"}
        onConfirm={handleConfirmDeleteOrRestore}
        onCancel={() => setConfirmOpen(false)}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default List;
